/**
 * "[1]" in an answer becomes a numbered pill that opens the cited document (from #574). v2 renders
 * markdown through React, so the markers are turned into elements while the markdown is built, and
 * React owns them like any other node.
 *
 * Markers inside links and code stay as written. Only numbers the answer actually cites become
 * pills (see CitePill): a bare "[3]" in other text is left alone.
 */
const MARKER = /\[(\d{1,3})\]/g;
const SKIPPED_TAGS = new Set(["a", "code", "pre"]);

const splitText = (value) => {
  const matches = [...value.matchAll(MARKER)];
  if (!matches.length) {
    return null;
  }

  const { nodes, end } = matches.reduce(
    (acc, { 0: marker, 1: n, index }) => ({
      nodes: [
        ...acc.nodes,
        ...(index > acc.end ? [{ type: "text", value: value.slice(acc.end, index) }] : []),
        { type: "element", tagName: "span", properties: { dataCite: n }, children: [{ type: "text", value: marker }] },
      ],
      end: index + marker.length,
    }),
    { nodes: [], end: 0 }
  );

  return end < value.length ? [...nodes, { type: "text", value: value.slice(end) }] : nodes;
};

const wrapMarkers = (node) => {
  if (!node.children || (node.type === "element" && SKIPPED_TAGS.has(node.tagName))) {
    return node;
  }

  return {
    ...node,
    children: node.children.flatMap((child) => (child.type === "text" ? splitText(child.value) || [child] : [wrapMarkers(child)])),
  };
};

/** Rehype plugin: wraps each [n] in <span data-cite="n">. */
export const rehypeCitePills = () => (tree) => wrapMarkers(tree);

/** The cited number of a span made by rehypeCitePills, or null. */
export const citeNumberOf = (props) => {
  const n = Number(props?.["data-cite"] ?? props?.node?.properties?.dataCite);

  return Number.isInteger(n) && n > 0 ? n : null;
};
